import { useCallback, useEffect, useState } from "react";
import { it } from "date-fns/locale";
import { CalendarDays, Plus, Search, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { fmtDate } from "../lib/roles";
import { Field, Panel } from "./common";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const iso = (d) => (d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}` : "");
const dmy = (s) => (s ? s.split("-").reverse().join("/") : "");
const Sel = ({ value, onChange, opts, empty, testid }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} className="h-10 w-full rounded-md border px-3 bg-white text-sm" data-testid={testid}>
    <option value="">{empty}</option>{opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
  </select>
);

export function DatePick({ value, onChange, placeholder, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="h-10 w-full rounded-md border bg-white px-3 text-sm flex items-center gap-2 text-left" data-testid={testid}>
          <CalendarDays className="w-4 h-4 text-[var(--teal)]" /><span className={value ? "text-[var(--navy)]" : "text-slate-400"}>{value ? dmy(value) : placeholder}</span>
          {value && <X className="w-3.5 h-3.5 ml-auto text-slate-400" onClick={(e) => { e.stopPropagation(); onChange(""); }} />}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar locale={it} weekStartsOn={1} mode="single" selected={value ? new Date(`${value}T12:00`) : undefined} onSelect={(d) => { onChange(iso(d)); setOpen(false); }} />
      </PopoverContent>
    </Popover>
  );
}

export function useMaintRefs() {
  const [r, setR] = useState(null);
  useEffect(() => {
    Promise.all(["/maint/options", "/maint/areas", "/maint/impianti", "/maint/beni-mobili"].map((p) => api(p).catch(() => ({}))))
      .then(([o, a, i, b]) => setR({ options: o, areas: a.areas || [], impianti: i.impianti || [], beni: b.beni || b.beni_mobili || [] }));
  }, []);
  return r;
}

export function InterventoForm({ refs, onClose, onSaved }) {
  const [f, setF] = useState({ area_id: "", impianto_id: "", titolo: "", priorita: "Normale", tipo: "", ditta: "", intervento_diretto: false, dIni: "", orario_inizio: "", dFin: "", orario_fine: "", descrizione: "" });
  const set = (k) => (v) => setF({ ...f, [k]: v?.target ? v.target.value : v });
  const imps = refs.impianti.filter((i) => !f.area_id || i.area_id === f.area_id);
  const save = async () => {
    if (!f.impianto_id || !f.titolo.trim()) return toast.error("Scegli l'impianto e scrivi il titolo");
    const { area_id, dIni, dFin, ...rest } = f;
    try { await api("/maint/schede", { method: "POST", body: { ...rest, data_inizio: dmy(dIni), data_fine: dmy(dFin) } }); toast.success("Intervento inserito"); onSaved(); } catch (e) { toast.error(e.message); }
  };
  return (
    <Dialog open onOpenChange={onClose}><DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="intervento-form"><DialogHeader><DialogTitle>Nuovo intervento di manutenzione</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Immobile / area"><Sel value={f.area_id} onChange={(v) => setF({ ...f, area_id: v, impianto_id: "" })} opts={refs.areas.map((a) => [a.id, [a.posizione, a.descrizione].filter(Boolean).join(" · ")])} empty="Tutti" testid="int-area" /></Field>
          <Field label="Impianto *"><Sel value={f.impianto_id} onChange={set("impianto_id")} opts={imps.map((i) => [i.id, i.descrizione])} empty="Seleziona..." testid="int-impianto" /></Field>
        </div>
        <Field label="Titolo *"><Input value={f.titolo} onChange={set("titolo")} data-testid="int-titolo" /></Field>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Priorità"><Sel value={f.priorita} onChange={set("priorita")} opts={(refs.options.priorita || []).map((p) => [p, p])} empty="—" testid="int-priorita" /></Field>
          <Field label="Tipo"><Sel value={f.tipo} onChange={set("tipo")} opts={(refs.options.tipi || []).map((p) => [p, p])} empty="—" testid="int-tipo" /></Field>
        </div>
        <div className="grid sm:grid-cols-[1fr,auto] gap-3 items-end">
          <Field label="Ditta esecutrice"><Input value={f.ditta} onChange={set("ditta")} disabled={f.intervento_diretto} data-testid="int-ditta" /></Field>
          <label className="flex items-center gap-2 text-sm pb-2"><Switch checked={f.intervento_diretto} onCheckedChange={set("intervento_diretto")} data-testid="int-diretto" />Intervento diretto</label>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Inizio previsto"><DatePick value={f.dIni} onChange={set("dIni")} placeholder="Scegli data" testid="int-data-inizio" /></Field>
          <Field label="Ora inizio"><Input type="time" value={f.orario_inizio.replace(".", ":")} onChange={(e) => setF({ ...f, orario_inizio: e.target.value.replace(":", ".") })} data-testid="int-ora-inizio" /></Field>
          <Field label="Fine prevista"><DatePick value={f.dFin} onChange={set("dFin")} placeholder="Scegli data" testid="int-data-fine" /></Field>
          <Field label="Ora fine"><Input type="time" value={f.orario_fine.replace(".", ":")} onChange={(e) => setF({ ...f, orario_fine: e.target.value.replace(":", ".") })} data-testid="int-ora-fine" /></Field>
        </div>
        <Field label="Descrizione"><Textarea rows={4} value={f.descrizione} onChange={set("descrizione")} data-testid="int-descrizione" /></Field>
        <Button onClick={save} className="w-full rounded-full bg-[var(--teal)]" data-testid="int-save"><Plus className="w-4 h-4 mr-1" />Inserisci intervento</Button>
      </div>
    </DialogContent></Dialog>
  );
}

export function InterventiSearch({ refs, onResults, reloadKey }) {
  const [f, setF] = useState({ area_id: "", impianto_id: "", bene_id: "", dal: "", al: "", q: "" });
  const run = useCallback(() => {
    const qs = new URLSearchParams(Object.entries(f).filter(([, v]) => v)).toString();
    api(`/maint/interventi?${qs}`).then((d) => onResults(d.interventi || [])).catch((e) => { toast.error(e.message); onResults([]); });
  }, [f, onResults]);
  useEffect(() => { run(); }, [reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const imps = refs.impianti.filter((i) => !f.area_id || i.area_id === f.area_id);
  return (
    <Panel className="mb-6 space-y-3" data-testid="interventi-search">
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <Field label="Immobile / area"><Sel value={f.area_id} onChange={(v) => setF({ ...f, area_id: v, impianto_id: "", bene_id: "" })} opts={refs.areas.map((a) => [a.id, [a.posizione, a.descrizione].filter(Boolean).join(" · ")])} empty="Tutti" testid="search-area" /></Field>
        <Field label="Impianto"><Sel value={f.impianto_id} onChange={(v) => setF({ ...f, impianto_id: v, bene_id: "" })} opts={imps.map((i) => [i.id, i.descrizione])} empty="Tutti" testid="search-impianto" /></Field>
        <Field label="Bene mobile"><Sel value={f.bene_id} onChange={(v) => setF({ ...f, bene_id: v, area_id: "", impianto_id: "" })} opts={refs.beni.map((b) => [b.id, b.nome])} empty="Tutti" testid="search-bene" /></Field>
        <Field label="Dal"><DatePick value={f.dal} onChange={(v) => setF({ ...f, dal: v })} placeholder="Data inizio" testid="search-dal" /></Field>
        <Field label="Al"><DatePick value={f.al} onChange={(v) => setF({ ...f, al: v })} placeholder="Data fine" testid="search-al" /></Field>
        <Field label="Testo"><Input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="Titolo, ditta, note…" data-testid="search-q" /></Field>
      </div>
      <Button onClick={run} className="rounded-full bg-[var(--navy)]" data-testid="search-run"><Search className="w-4 h-4 mr-1" />Cerca interventi</Button>
    </Panel>
  );
}

export const whenLabel = (x) => (x.data_ref ? fmtDate(x.data_ref) : "");
